import React, { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { MotifDropdown } from '../dropdown/dropdown';
import { MotifIconButton } from '../button/icon-button/icon-button';
import { ReactComponent as NavigationIcMoreHoriz24px } from '../../assets/icons/navigation/ic_more_horiz_24px.svg';
import { ReactComponent as NavigationIcChevronRight24px } from '../../assets/icons/navigation/ic_chevron_right_24px.svg';

let nextUniqueId;
nextUniqueId = 0;

const defaultIcons = {
  separator: <NavigationIcChevronRight24px />
};

/**
 * @typedef Props
 * @prop  {React.ReactNode} children
 * @prop  {number} [firstItemsAmount]
 * @prop  {{dropdown: React.ReactNode, separator: React.ReactNode}} [icons]
 * @prop  {string} [className]
 * @prop {number} [lastItemsAmount]
 * @prop {number} [maxItems]
 * @prop {{}} [triggerButtonProps]
 */
/**
 * @param {Props & {[x: string]: any;}} props
 */
const MotifBreadcrumb = (props) => {
  const {
    children = null,
    className = '',
    firstItemsAmount = 0,
    icons: userIcons = {},
    lastItemsAmount = 0,
    maxItems = null,
    triggerButtonProps = {},
    ...other
  } = props;
  const triggerId = `breadcrumb-dropdown-trigger-${nextUniqueId++}`;
  const dropdownId = `breadcrumb-dropdown-${nextUniqueId++}`;

  const icons = {
    ...defaultIcons,
    ...userIcons
  };

  if (children === null || children.length === 0) {
    return false;
  }

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const closeDropdown = useCallback(() => setIsDropdownOpen(false), []);
  const handleOnClick = useCallback(() => setIsDropdownOpen(!isDropdownOpen), [isDropdownOpen]);

  const classes = classNames({
    'motif-breadcrumb': true,
    [className]: true
  });

  // creates a clone to add custom props
  const parsedChildren = useMemo(
    () =>
      React.Children.map(children, (child, index) => {
        const clonedItem = React.cloneElement(child, {
          itemIndex: index,
          itemsLength: children.length,
          isTruncated:
            maxItems < children.length &&
            (firstItemsAmount > 0 || lastItemsAmount > 0) &&
            index >= firstItemsAmount &&
            index < children.length - lastItemsAmount
        });

        return clonedItem;
      }),
    [children, maxItems, firstItemsAmount, lastItemsAmount]
  );

  const firstChildren = parsedChildren.slice(0, firstItemsAmount);

  const truncatedChildren = parsedChildren.filter(currentChild => currentChild.props.isTruncated);

  const lastChildren = parsedChildren.slice(
    parsedChildren.length - lastItemsAmount,
    parsedChildren.length
  );

  const renderDropdown = (
    <li className="motif-breadcrumb-list-item">
      {firstChildren.length > 0 && (
        <span className="motif-breadcrumb-separator">{icons.separator}</span>
      )}
      <MotifDropdown
        open={isDropdownOpen}
        placement="bottom-left"
        id={dropdownId}
        aria-labelledby={triggerId}
        handleClickOutside={closeDropdown}
        trigger={
          <MotifIconButton
            type="button"
            onClick={handleOnClick}
            aria-controls={dropdownId}
            className="breadcrumb-dropdown-trigger"
            id={triggerId}
            aria-expanded={isDropdownOpen}
            aria-label="dropdown trigger"
            {...triggerButtonProps}
          >
            <NavigationIcMoreHoriz24px className="motif-breadcrumb-dropdown-icon" />
          </MotifIconButton>
        }
      >
        {truncatedChildren}
      </MotifDropdown>
    </li>
  );

  return (
    <nav className={classes} aria-label="breadcrumb" {...other} data-motif-breadcrumb>
      {maxItems < children.length &&
      (firstItemsAmount > 0 || lastItemsAmount > 0) &&
      firstItemsAmount + lastItemsAmount <= children.length ? (
        <ol className="motif-breadcrumb-list">
          {firstChildren}
          {truncatedChildren.length > 0 && renderDropdown}
          {lastChildren}
        </ol>
      ) : (
        <ol className="motif-breadcrumb-list">{parsedChildren}</ol>
      )}
    </nav>
  );
};

MotifBreadcrumb.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  firstItemsAmount: PropTypes.number,
  icons: PropTypes.shape({
    dropdown: PropTypes.node,
    separator: PropTypes.node
  }),
  lastItemsAmount: PropTypes.number,
  maxItems: PropTypes.number,
  triggerButtonProps: PropTypes.instanceOf(Object)
};

export { MotifBreadcrumb };
